import { useState, useEffect } from "react";
import { buildRequest } from "../../utils/openHouseOpportunityHelpers";
import { addOpportunityRequest } from "../../utils/firebasefunctions";
import { notifyLcOnOhRequest } from "../../utils/helper";
import { emailApproversOnOhRequest } from "../../utils/openHouseRequestEmail";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";

export default function RequestToHostModal({ open, opportunity, onClose, onSubmitted }) {
  const [{ user, users }] = useStateValue();
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("14:00");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate("");
    setStartTime("12:00");
    setEndTime("14:00");
    setNote("");
  }, [open, opportunity?.id]);

  if (!open || !opportunity) return null;

  const handleSubmit = async () => {
    if (!date || !startTime || !endTime) {
      toast.error("Please choose a date and time.");
      return;
    }
    setSaving(true);
    try {
      const request = buildRequest({
        id: crypto.randomUUID(),
        agentId: user.id,
        agentName: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
        agentEmail: user.email || "",
        proposedDate: date,
        proposedStartTime: startTime,
        proposedEndTime: endTime,
        note,
      });
      await addOpportunityRequest(opportunity.id, request);
      notifyLcOnOhRequest(opportunity, request, users).catch((err) =>
        console.error("notifyLcOnOhRequest failed:", err),
      );
      // Same audience as the bell, by email (Henry, 2026-08-31).
      emailApproversOnOhRequest({
        opportunity,
        request,
        allUsers: users,
        sender: user,
      }).catch((err) => console.error("OH request email failed:", err));
      toast.success("Request sent for approval.");
      onSubmitted && onSubmitted();
      onClose();
    } catch (err) {
      console.error("Request to host failed:", err);
      toast.error(err.message || "Could not send request. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Request to Host</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">
              {opportunity.propertyAddress?.split(",")[0]}
            </span>
            {opportunity.suggestedTiming ? ` · ${opportunity.suggestedTiming}` : ""}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Start</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">End</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Note (Optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Anything the coordinator should know…"
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50">
            {saving ? "Sending…" : "Send Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
